import { useState, useEffect, useCallback } from "react";
import { T, STATE, MARKET, canAccess, getStateConfig } from "../../config/theme.js";
import { api, trackEvent } from "../../hooks/useApi.js";
import { Btn, StateBadge, Input } from "../ui/index.jsx";
import { Trash2, ArrowRightLeft, Eye } from "lucide-react";
import DetailOverview from "./DetailOverview.jsx";
import DetailFundamentals from "./DetailFundamentals.jsx";
import DetailContext from "./DetailContext.jsx";
import PrincipleSettings from "../modals/PrincipleSettings.jsx";

const TABS = [
  { key: "overview", label: "요약" },
  { key: "fundamentals", label: "펀더멘탈" },
  { key: "context", label: "맥락" },
];

export default function Detail({ holding, report, onBack, goQA, token, plan, onUpgrade, onAction, onDelete }) {
  const isWatchlist = !!holding?._watchlist;
  const watchEntry = holding?._watchEntry || {};

  // 대기종목 → 보유종목 전환 모달
  const [showConvert, setShowConvert] = useState(false);
  const [convertPrice, setConvertPrice] = useState("");
  const [convertQty, setConvertQty] = useState("");
  const [converting, setConverting] = useState(false);

  // ★ FIX: 모든 훅을 early return 위로 이동 (#310 방지)
  /* ── 탭 상태 (URL hash 연동) ── */
  const getTabFromHash = () => {
    const hash = window.location.hash.replace("#", "");
    return TABS.find(t => t.key === hash)?.key || "overview";
  };
  const [activeTab, setActiveTab] = useState(getTabFromHash);

  useEffect(() => {
    const onHash = () => setActiveTab(getTabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const switchTab = useCallback((key) => {
    window.location.hash = key;
    setActiveTab(key);
    trackEvent("detail.tab", { symbol: holding.symbol, tab: key });
  }, [holding.symbol]);

  /* ── 투자 설정 상태 ── */
  const [editingStyle, setEditingStyle] = useState(false);
  const [showPrinciple, setShowPrinciple] = useState(false);
  const [investPeriod, setInvestPeriod] = useState(holding.prefs?.investment_period || holding.prefs?.investment_style || "long_term");
  const [investStrategy, setInvestStrategy] = useState(holding.prefs?.investment_strategy || "general");
  const [savingStyle, setSavingStyle] = useState(false);

  /* ── 불안 패턴 감지 ── */
  const [anxietyLevel, setAnxietyLevel] = useState(null);

  /* ── 리포트 수동 생성 ── */
  const [reportGenerating, setReportGenerating] = useState(false);

  /* ── 강화 분석 (SniperX 데이터) ── */
  const [enhanced, setEnhanced] = useState(null);
  const [enhancedLoading, setEnhancedLoading] = useState(false);

  useEffect(() => {
    if (!report) return;
    trackEvent("report.view", { symbol: holding.symbol, report_snapshot_id: report?.id });
    if (report?.payload?.state_changed) {
      trackEvent("state_change.seen", { symbol: holding.symbol, meta: { from: report.payload.prev_state, to: report.state_stock } });
    }
    // 강화 분석 로드
    if (token && holding.symbol) {
      setEnhancedLoading(true);
      const regime = report?.state_market || "NEUTRAL";
      api(`/enhanced/${holding.symbol}?regime=${regime}`, {}, token)
        .then(data => setEnhanced(data))
        .catch(() => setEnhanced(null))
        .finally(() => setEnhancedLoading(false));
    }
    if (!token || !holding.symbol) return;
    api("/qa/sessions", {}, token).then(sessions => {
      const stockSessions = (sessions || []).filter(s => s.symbol === holding.symbol);
      const recentSessions = stockSessions.filter(s => {
        const age = Date.now() - new Date(s.created_at).getTime();
        return age < 7 * 24 * 60 * 60 * 1000;
      });
      if (recentSessions.length >= 5) setAnxietyLevel("high");
      else if (recentSessions.length >= 3) setAnxietyLevel("mid");
    }).catch(() => {});
  }, [holding.symbol, token, report]);

  /* ── 리포트 없음 ── */
  if (!report) return (
    <div>
      <Btn variant="ghost" onClick={onBack}>← 돌아가기</Btn>
      <div style={{ textAlign: "center", padding: 80 }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📊</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>
          {holding.name}의 리포트가 아직 없습니다
        </div>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 24, lineHeight: 1.6 }}>
          리포트를 생성하면 상태 라벨, 데이터 분석, 변화 추이를 확인할 수 있습니다.
        </div>
        <button
          disabled={reportGenerating}
          onClick={async () => {
            setReportGenerating(true);
            try {
              await api("/test/report/am", { method: "POST" }, token);
              // 리포트 생성 후 대시보드로 돌아가서 새로고침
              alert("리포트 생성이 완료되었습니다. 대시보드에서 확인하세요.");
              onBack?.();
            } catch (e) {
              alert("리포트 생성 실패: " + e.message);
            }
            setReportGenerating(false);
          }}
          style={{
            background: T.accent, color: "#fff", border: "none",
            borderRadius: 12, padding: "14px 32px", fontSize: 14,
            fontWeight: 700, cursor: reportGenerating ? "not-allowed" : "pointer",
            fontFamily: "inherit", opacity: reportGenerating ? 0.6 : 1,
            transition: "all 0.2s",
          }}
        >
          {reportGenerating ? "리포트 생성 중... (최대 1분)" : "리포트 생성"}
        </button>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 12 }}>
          공개 데이터를 수집하고 분석합니다
        </div>
      </div>
    </div>
  );

  /* ── 탭 인디케이터 위치 계산 ── */
  const tabIdx = TABS.findIndex(t => t.key === activeTab);

  const m = report.micro || {};
  const p = report.payload || {};
  const sc = getStateConfig(report.state_stock);

  const PERIOD_LABELS = { long_term: "장기", mid_term: "중장기", swing: "스윙" };
  const STRATEGY_LABELS = { general: "일반", dividend: "배당", value: "가치", growth: "성장" };

  const saveInvestStyle = async () => {
    setSavingStyle(true);
    try {
      const newPrefs = { ...(holding.prefs || {}), investment_period: investPeriod, investment_strategy: investStrategy };
      await api(`/holdings/${holding.id}`, { method: "PATCH", body: JSON.stringify({ prefs: newPrefs }) }, token);
      holding.prefs = newPrefs;
      setEditingStyle(false);
    } catch (e) {
      alert("저장 실패: " + e.message);
    }
    setSavingStyle(false);
  };

  /* ── 공통 props ── */
  const sharedProps = { holding, report, m, p, plan, onUpgrade, token, anxietyLevel, onAction, isWatchlist, watchEntry };

  return (
    <div>
      {/* 원칙 설정 모달 (보유종목만) */}
      {!isWatchlist && showPrinciple && (
        <PrincipleSettings
          holding={holding}
          token={token}
          onSave={() => {}}
          onClose={() => setShowPrinciple(false)}
        />
      )}

      {/* 보유 전환 모달 (대기종목만) */}
      {isWatchlist && showConvert && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(5,5,10,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card || T.bg, borderRadius: 20, border: `1px solid ${T.border}`, maxWidth: 400, width: "100%", padding: "28px 24px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>보유종목으로 전환</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 20 }}>{holding.name} ({holding.symbol})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>매수 단가</div>
                <Input type="number" placeholder="예: 52000" value={convertPrice} onChange={e => setConvertPrice(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>수량</div>
                <Input type="number" placeholder="예: 10" value={convertQty} onChange={e => setConvertQty(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <Btn variant="ghost" style={{ flex: 1, justifyContent: "center", padding: 14 }} onClick={() => setShowConvert(false)}>취소</Btn>
              <Btn variant="primary" style={{ flex: 2, justifyContent: "center", padding: 14 }} disabled={converting || !convertPrice || !convertQty} onClick={async () => {
                setConverting(true);
                try {
                  await api(`/watchlist/${holding.symbol}/convert`, { method: "POST", body: JSON.stringify({ avg_price: parseFloat(convertPrice), qty: parseInt(convertQty) }) }, token);
                  alert("보유종목으로 전환되었습니다.");
                  onBack?.();
                } catch (e) { alert("전환 실패: " + e.message); }
                setConverting(false);
              }}>{converting ? "전환 중..." : "매수 완료 — 보유종목으로 전환"}</Btn>
            </div>
          </div>
        </div>
      )}
      {/* ── Sticky 헤더 영역 ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 20,
        background: T.bgCard || T.bg,
        paddingBottom: 0,
        borderBottom: `1px solid ${T.border}`,
        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
      }}>
        {/* 상단 네비게이션 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, paddingBottom: 8 }}>
          <Btn variant="ghost" onClick={onBack}>← 돌아가기</Btn>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Btn variant="ghost" style={{ color: T.textMuted, padding: "6px 10px" }} onClick={() => {
              const msg = isWatchlist
                ? `${holding.name}을(를) 대기종목에서 삭제할까요?`
                : `${holding.name}을(를) 포트폴리오에서 삭제할까요?`;
              if (confirm(msg)) {
                if (isWatchlist) {
                  api(`/watchlist/${holding.symbol}`, { method: "DELETE" }, token)
                    .then(() => onBack?.())
                    .catch(e => alert("삭제 실패: " + e.message));
                } else {
                  onDelete?.(holding.id);
                }
              }
            }}><Trash2 size={15} /></Btn>
            <Btn variant="small" onClick={() => goQA(holding)}>질문하기</Btn>
          </div>
        </div>

        {/* 종목명 + 상태 */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: T.text, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "45%" }}>{holding.name}</h2>
          <StateBadge state={report.state_stock} size="lg" />
          <span style={{ fontSize: 11, color: (MARKET[report.state_market] || MARKET.NEUTRAL).color, fontWeight: 600, whiteSpace: "nowrap" }}>
            {(MARKET[report.state_market] || MARKET.NEUTRAL).label}
          </span>
        </div>

        {/* 심볼 + 리포트 시간 */}
        <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10 }}>
          {holding.symbol} · {report.run_type} {new Date(report.snapshot_at).toLocaleString("ko-KR")}
          {report.state_event && report.state_event !== "DART_NONE" && (
            <span style={{ color: T.yellow, marginLeft: 12 }}>
              DART {report.state_event === "DART_HIGH" ? "HIGH (중요 공시)" : report.state_event === "DART_MED" ? "MED (참고 공시)" : "LOW (일반 공시)"}
            </span>
          )}
        </div>

        {/* 투자 설정 / 대기종목 정보 */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {isWatchlist ? (
            <>
              <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: T.textMuted + "15", color: T.textMuted }}>대기</span>
              {(() => {
                const watchStatusMeta = {
                  WATCHING: { label: "관망", color: T.textMuted },
                  PREPARING: { label: "준비", color: T.yellow },
                  APPROACHING: { label: "조건 근접", color: T.green },
                  EXCLUDED: { label: "제외 고려", color: T.red },
                };
                const st = watchStatusMeta[watchEntry.status] || watchStatusMeta.WATCHING;
                return <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: st.color + "15", color: st.color }}>{st.label}</span>;
              })()}
              <span onClick={() => setShowConvert(true)} style={{ fontSize: 11, color: T.green, cursor: "pointer", padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.green}40`, fontWeight: 600, transition: "all 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = T.green + "10"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                매수 완료 → 보유 전환
              </span>
            </>
          ) : (
            <>
              {!editingStyle ? (
                <>
                  <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: T.accent + "15", color: T.accent }}>
                    {PERIOD_LABELS[holding.prefs?.investment_period || holding.prefs?.investment_style] || "장기"}
                  </span>
                  {holding.prefs?.investment_strategy && holding.prefs.investment_strategy !== "general" && (
                    <span style={{ padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: T.yellow + "15", color: T.yellow }}>
                      {STRATEGY_LABELS[holding.prefs.investment_strategy]}
                    </span>
                  )}
                  <span onClick={() => setEditingStyle(true)} style={{ fontSize: 11, color: T.textMuted, cursor: "pointer", padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.border}`, transition: "all 0.15s" }} onMouseEnter={e => e.currentTarget.style.borderColor = T.accent} onMouseLeave={e => e.currentTarget.style.borderColor = T.border}>
                    변경
                  </span>
                  <span onClick={() => setShowPrinciple(true)} style={{ fontSize: 11, color: T.accent, cursor: "pointer", padding: "4px 8px", borderRadius: 6, border: `1px solid ${T.accent}40`, transition: "all 0.15s", fontWeight: 600 }} onMouseEnter={e => e.currentTarget.style.background = T.accent + "10"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    원칙 설정
                  </span>
                </>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <select value={investPeriod} onChange={e => setInvestPeriod(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 12, outline: "none" }}>
                    <option value="long_term">장기 (6개월+)</option>
                    <option value="mid_term">중장기 (1~6개월)</option>
                    <option value="swing">스윙 (1~4주)</option>
                  </select>
                  <select value={investStrategy} onChange={e => setInvestStrategy(e.target.value)} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 12, outline: "none" }}>
                    <option value="general">일반</option>
                    <option value="dividend">배당</option>
                    <option value="value">가치</option>
                    <option value="growth">성장</option>
                  </select>
                  <Btn variant="success" style={{ padding: "4px 12px", fontSize: 11 }} onClick={saveInvestStyle}>{savingStyle ? "저장 중..." : "저장"}</Btn>
                  <Btn variant="ghost" style={{ padding: "4px 12px", fontSize: 11 }} onClick={() => setEditingStyle(false)}>취소</Btn>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── 탭 바 ── */}
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", gap: 0 }}>
            {TABS.map((tab, i) => (
              <button
                key={tab.key}
                onClick={() => switchTab(tab.key)}
                style={{
                  flex: 1,
                  padding: "10px 0",
                  fontSize: 14,
                  fontWeight: activeTab === tab.key ? 800 : 500,
                  color: activeTab === tab.key ? T.accent : T.textDim,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  transition: "color 0.2s",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* 인디고 언더라인 슬라이드 */}
          <div style={{
            position: "absolute",
            bottom: 0,
            left: `${(tabIdx / TABS.length) * 100}%`,
            width: `${100 / TABS.length}%`,
            height: 3,
            background: T.accent,
            borderRadius: "3px 3px 0 0",
            transition: "left 0.2s ease",
          }} />
        </div>
      </div>

      {/* ── 탭 콘텐츠 ── */}
      <div style={{ paddingTop: 16 }}>
        {/* 대기종목 전용: 나의 진입 논리 (요약 탭에서만) */}
        {isWatchlist && activeTab === "overview" && (
          <div style={{ marginBottom: 16, padding: "16px 18px", background: T.bg, borderRadius: 12, border: `1px dashed ${T.textMuted}30` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Eye size={16} color={T.textMuted} />
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>나의 진입 논리</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ padding: "10px 14px", background: T.card || T.bgCard || T.bg, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>감시 이유</div>
                <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{watchEntry.watch_reason || "미입력"}</div>
              </div>
              {watchEntry.entry_conditions && (
                <div style={{ padding: "10px 14px", background: T.card || T.bgCard || T.bg, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>진입 조건</div>
                  <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6 }}>{watchEntry.entry_conditions}</div>
                </div>
              )}
              {watchEntry.target_price && (
                <div style={{ padding: "10px 14px", background: T.card || T.bgCard || T.bg, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>목표 매수가</div>
                    <div className="mono-num" style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{Number(watchEntry.target_price).toLocaleString()}원</div>
                  </div>
                  {report?.micro?.close > 0 && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>현재가 대비</div>
                      <div className="mono-num" style={{ fontSize: 15, fontWeight: 700, color: report.micro.close <= Number(watchEntry.target_price) ? T.green : T.textDim }}>
                        {((report.micro.close - Number(watchEntry.target_price)) / Number(watchEntry.target_price) * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10, lineHeight: 1.5 }}>
              위 데이터 분석 결과와 나의 진입 논리를 비교해서, 시나리오가 여전히 유효한지 스스로 점검해보세요.
            </div>
          </div>
        )}

        {/* ── 강화 분석 카드 (overview 탭에서만) ── */}
        {activeTab === "overview" && enhanced && enhanced.cards && enhanced.cards.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {/* ═══ Level 1: 종합 해설 (Hero, 카드 없음) ═══ */}
            {(() => {
              const ov = enhanced.overview || { headline: enhanced.summary, tone: "neutral" };
              const toneDot = {
                stable: T.green, change: T.yellow, volatile: T.red, active: T.yellow,
                mixed: T.yellow, event: T.blue, flow: T.yellow, neutral: T.textMuted,
              };
              return (
                <div style={{ padding: "24px 4px 22px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{
                      width: 9, height: 9, borderRadius: "50%", flexShrink: 0, marginTop: 11,
                      background: toneDot[ov.tone] || T.textMuted,
                    }} />
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: T.text, lineHeight: 1.4, letterSpacing: "-0.01em" }}>
                        {ov.headline}
                      </div>
                      {ov.detail && (
                        <div style={{ fontSize: 14, color: T.textDim, lineHeight: 1.6, marginTop: 8 }}>
                          {ov.detail}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* 5개 카드 */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {enhanced.cards.map((card, i) => {
                const signalColors = {
                  green: { bg: T.green + "12", border: T.green + "30", dot: T.green },
                  yellow: { bg: T.yellow + "12", border: T.yellow + "30", dot: T.yellow },
                  red: { bg: T.red + "12", border: T.red + "30", dot: T.red },
                  neutral: { bg: T.bg, border: T.border, dot: T.textMuted },
                };
                const sc = signalColors[card.signal] || signalColors.neutral;

                return (
                  <details key={i} style={{
                    background: T.card, borderRadius: 12, border: `1px solid ${T.border}`,
                    overflow: "hidden",
                  }}>
                    <summary style={{
                      padding: "14px 18px", cursor: "pointer", listStyle: "none",
                      display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: "50%", background: sc.dot, flexShrink: 0,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{card.cardTitle}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.text, lineHeight: 1.3 }}>{card.title}</div>
                      </div>
                      <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0, alignSelf: "center" }}>자세히</span>
                    </summary>
                    <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${T.border}` }}>
                      <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.8, marginTop: 12 }}>
                        {card.description}
                      </div>
                      {card.whyText && (
                        <div style={{
                          marginTop: 10, padding: "10px 14px", background: T.bg,
                          borderRadius: 8, fontSize: 11, color: T.textMuted, lineHeight: 1.7,
                        }}>
                          왜 이렇게 표시됐나요? — {card.whyText}
                        </div>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>

            {/* 데이터 충돌 감지 — Level 1 종합 해설이 mixed 톤이면 위에 이미 같은 내용 → 중복 숨김 */}
            {enhanced.conflicts && enhanced.conflicts.length > 0 && enhanced.overview?.tone !== "mixed" && (
              <div style={{ marginTop: 12 }}>
                {enhanced.conflicts.map((c, i) => (
                  <div key={i} style={{
                    padding: "14px 18px", borderRadius: 12, marginBottom: 8,
                    background: T.yellow + "10", border: `1px solid ${T.yellow}25`,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.yellow, marginBottom: 4 }}>⚠️ {c.title}</div>
                    <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.7 }}>{c.description}</div>
                  </div>
                ))}
              </div>
            )}

            {/* 확인할 질문 */}
            {enhanced.checkQuestions && enhanced.checkQuestions.length > 0 && (
              <div style={{
                marginTop: 12, padding: "14px 18px", borderRadius: 12,
                background: T.accent + "08", border: `1px solid ${T.accent}20`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 10 }}>확인해볼 질문</div>
                {enhanced.checkQuestions.map((q, i) => (
                  <div key={i} style={{
                    fontSize: 13, color: T.textDim, lineHeight: 1.7,
                    display: "flex", alignItems: "flex-start", gap: 8,
                    marginBottom: i < enhanced.checkQuestions.length - 1 ? 8 : 0,
                  }}>
                    <span style={{ color: T.accent, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                    <span>{q}</span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Phase 2: 자세히 보기 섹션 ── */}
            {enhanced.details && (
              <details style={{
                marginTop: 14, background: T.card, borderRadius: 12,
                border: `1px solid ${T.border}`, overflow: "hidden",
              }}>
                <summary style={{
                  padding: "14px 18px", cursor: "pointer", listStyle: "none",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>재무 · 업종 · 계절성 더 보기</div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>이 종목의 장기 흐름을 함께 살펴봐요</div>
                  </div>
                  <span style={{ fontSize: 11, color: T.textMuted }}>펼치기</span>
                </summary>
                <div style={{ padding: "4px 18px 18px", borderTop: `1px solid ${T.border}`, display: "flex", flexDirection: "column", gap: 18 }}>

                  {/* 시장 환경 (날씨형) */}
                  {enhanced.details.marketWeather && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>{enhanced.details.marketWeather.sectionTitle}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 22 }}>{enhanced.details.marketWeather.icon}</span>
                        <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{enhanced.details.marketWeather.label}</span>
                      </div>
                      <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>{enhanced.details.marketWeather.note}</div>
                    </div>
                  )}

                  {/* 재무 추이 */}
                  {enhanced.details.financialTrend && (
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{enhanced.details.financialTrend.sectionTitle}</div>
                      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10, lineHeight: 1.5 }}>
                        과거 20개 분기 매출 흐름이에요. 오른쪽 끝(진한 색)이 가장 최근 분기예요.
                      </div>
                      {/* 매출 미니 막대 그래프 */}
                      {(() => {
                        const ft = enhanced.details.financialTrend;
                        const vals = ft.series.map(s => s.revenue).filter(v => v != null);
                        const max = Math.max(...vals, 1);
                        return (
                          <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 60, marginBottom: 10 }}>
                            {ft.series.map((s, i) => {
                              const h = s.revenue != null ? Math.max((s.revenue / max) * 56, 2) : 2;
                              const isLast = i === ft.series.length - 1;
                              return (
                                <div key={i} title={`${s.label}: ${s.revenue != null ? s.revenue.toLocaleString() + "억" : "-"}`}
                                  style={{ flex: 1, height: h, background: isLast ? T.accent : T.accent + "40", borderRadius: 2, minWidth: 4 }} />
                              );
                            })}
                          </div>
                        );
                      })()}
                      <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
                        <div>
                          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>최근 분기 매출</div>
                          <div className="mono-num" style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                            {enhanced.details.financialTrend.latest.revenue != null ? enhanced.details.financialTrend.latest.revenue.toLocaleString() + "억" : "-"}
                            {enhanced.details.financialTrend.latest.revenueYoY != null && (
                              <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 6, color: enhanced.details.financialTrend.latest.revenueYoY >= 0 ? T.green : T.red }} title="전년 동기 대비 (1년 전 같은 분기와 비교)">
                                전년 대비 {enhanced.details.financialTrend.latest.revenueYoY > 0 ? "+" : ""}{enhanced.details.financialTrend.latest.revenueYoY}%
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>최근 분기 영업이익</div>
                          <div className="mono-num" style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                            {enhanced.details.financialTrend.latest.operatingProfit != null ? enhanced.details.financialTrend.latest.operatingProfit.toLocaleString() + "억" : "-"}
                            {enhanced.details.financialTrend.latest.profitYoY != null && (
                              <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 6, color: enhanced.details.financialTrend.latest.profitYoY >= 0 ? T.green : T.red }} title="전년 동기 대비 (1년 전 같은 분기와 비교)">
                                전년 대비 {enhanced.details.financialTrend.latest.profitYoY > 0 ? "+" : ""}{enhanced.details.financialTrend.latest.profitYoY}%
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {enhanced.details.financialTrend.cashQuality && (
                        <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6, padding: "8px 12px", background: T.bg, borderRadius: 8, marginBottom: 6 }}>
                          {enhanced.details.financialTrend.cashQuality.note}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>{enhanced.details.financialTrend.note}</div>
                    </div>
                  )}

                  {/* 동종업계 비교 */}
                  {enhanced.details.peerRanking && (
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{enhanced.details.peerRanking.sectionTitle} · {enhanced.details.peerRanking.sectorName}</div>
                      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10, lineHeight: 1.5 }}>
                        같은 업종 안에서 이 종목이 어디쯤 있는지 보여줘요.
                      </div>
                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        {[
                          { label: "PER", val: enhanced.details.peerRanking.self.per, rank: enhanced.details.peerRanking.ranks.per },
                          { label: "PBR", val: enhanced.details.peerRanking.self.pbr, rank: enhanced.details.peerRanking.ranks.pbr },
                          { label: "시가총액", val: enhanced.details.peerRanking.self.marketCap, rank: enhanced.details.peerRanking.ranks.marketCap, isCap: true },
                        ].filter((m) => m.val != null).map((m, i) => (
                          <div key={i} style={{ flex: 1, padding: "10px 12px", background: T.bg, borderRadius: 8 }}>
                            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{m.label}</div>
                            <div className="mono-num" style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
                              {m.isCap ? m.val.toLocaleString() + "억" : m.val}
                            </div>
                            {m.rank && (
                              <div style={{ fontSize: 11, color: T.accent, marginTop: 2 }}>{m.rank.total}개 중 {m.rank.rank}위</div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>{enhanced.details.peerRanking.note}</div>
                    </div>
                  )}

                  {/* 계절성 패턴 */}
                  {enhanced.details.seasonality && (
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{enhanced.details.seasonality.sectionTitle}</div>
                      <div style={{ fontSize: 12, color: T.textDim, marginBottom: 10, lineHeight: 1.5 }}>
                        과거 5년간 월별 평균 가격 움직임이에요. <span style={{ color: T.green }}>위로 뻗은 초록</span>은 평균적으로 오른 달, <span style={{ color: T.red }}>아래로 뻗은 빨강</span>은 내린 달이에요.
                      </div>
                      {(() => {
                        const sn = enhanced.details.seasonality;
                        const maxAbs = Math.max(...sn.months.map(m => Math.abs(m.avgDaily)), 0.01);
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 3, height: 56, marginBottom: 10 }}>
                            {sn.months.map((m) => {
                              const h = (Math.abs(m.avgDaily) / maxAbs) * 24;
                              const up = m.avgDaily >= 0;
                              return (
                                <div key={m.month} title={`${m.month}월: ${m.avgDaily > 0 ? "+" : ""}${m.avgDaily}%`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                                  <div style={{ flex: 1, display: "flex", alignItems: "flex-end", width: "100%", justifyContent: "center" }}>
                                    {up && <div style={{ width: "70%", height: h, background: T.green + "70", borderRadius: "2px 2px 0 0" }} />}
                                  </div>
                                  <div style={{ flex: 1, display: "flex", alignItems: "flex-start", width: "100%", justifyContent: "center" }}>
                                    {!up && <div style={{ width: "70%", height: h, background: T.red + "70", borderRadius: "0 0 2px 2px" }} />}
                                  </div>
                                  <div style={{ fontSize: 8, color: T.textMuted }}>{m.month}</div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>{enhanced.details.seasonality.note}</div>
                    </div>
                  )}

                </div>
              </details>
            )}
          </div>
        )}

        {/* 강화 분석 로딩 */}
        {activeTab === "overview" && enhancedLoading && (
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <div style={{ fontSize: 12, color: T.textMuted }}>데이터 분석 중...</div>
          </div>
        )}

        {activeTab === "overview" && <DetailOverview {...sharedProps} />}
        {activeTab === "fundamentals" && <DetailFundamentals {...sharedProps} />}
        {activeTab === "context" && <DetailContext {...sharedProps} />}
      </div>

      {/* ── 대기종목 → 보유종목 전환 모달 ── */}
      {showConvert && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(5,5,10,0.85)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card || T.bgCard || T.bg, borderRadius: 20, border: `1px solid ${T.border}`, maxWidth: 400, width: "100%", padding: "28px 24px" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>보유종목으로 전환</div>
            <div style={{ fontSize: 13, color: T.textDim, marginBottom: 20 }}>{holding.name} ({holding.symbol})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>매수 단가</div>
                <Input type="number" placeholder="예: 52000" value={convertPrice} onChange={e => setConvertPrice(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>수량</div>
                <Input type="number" placeholder="예: 10" value={convertQty} onChange={e => setConvertQty(e.target.value)} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <Btn variant="ghost" style={{ flex: 1, justifyContent: "center", padding: 14 }} onClick={() => setShowConvert(false)}>취소</Btn>
              <Btn variant="primary" style={{ flex: 2, justifyContent: "center", padding: 14 }} disabled={converting || !convertPrice || !convertQty} onClick={async () => {
                setConverting(true);
                try {
                  await api(`/watchlist/${holding.symbol}/convert`, {
                    method: "POST",
                    body: JSON.stringify({ avg_price: parseFloat(convertPrice), qty: parseInt(convertQty) }),
                  }, token);
                  setShowConvert(false);
                  onBack?.();
                } catch (e) { alert("전환 실패: " + e.message); }
                setConverting(false);
              }}>{converting ? "전환 중..." : "매수 완료 — 보유종목으로 전환"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
